// @ts-strict-ignore
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import round from 'lodash/round';
import capitalize from 'lodash/capitalize';

import {
  TooltipBulletStyle,
  TooltipItem,
  TooltipItemType,
  TooltipRow,
  TooltipSection,
} from 'common/components/chart-tooltip/types';
import {EVENT_MATH_LABELS, FUNNELS_CONVERSION_RATES, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {FormulaClause, ReportSectionsMetadata, ShowClause} from 'common/report/insights/util/types';
import {
  BehaviorShowClause,
  ChartType,
  FUNNELS_MATH_TYPES,
  InsightsBookmarkParams,
  InsightsResourceType,
  MATH_TYPE,
  MetricType,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {
  computeDelta,
  formatDateRanges,
  groupEventComparisonShowMetadata,
  isIncompleteFirstInterval,
  isIncompleteLastInterval,
  NUMERIC_DATA_PRECISION,
} from 'common/report/insights/util';
import {commaizeNumber, formatMetricNumberWithUnits, formatPercent, isDefinedValue} from 'common/util';
import {pluralize, truncateMiddle} from 'common/util/string';
import {AbsoluteDateRange, formatDate, UNITS} from 'common/util/date';
import {reporter} from 'common/report/error-reporting';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {LiftType} from 'common/report/util/lift/types';
import {ForecastValue} from 'common/report/types';
import {isBehaviorShowClause, isFormulaShowClause} from 'common/report/insights/models/utils';
import {TEXT_HIGHLIGHT_THEME} from 'common/components/text-highlight';
import {FUNNELS_TIME_TO_CONVERSION} from 'common/query-builder/query-block/properties-menu/constants';
import {ChartTooltipProps} from 'common/components/chart-tooltip';
import {renderChartTooltipToString} from 'common/components/chart-tooltip/render-chart-tooltip-to-string';
import {LocalizationStore} from 'common/i18n/store';
import {TimeUnit, TimeUnitLabelTranslatable} from 'common/widgets/date-range-picker/models/types';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {Translatable} from 'common/i18n';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {isValidFormulaShowClause} from 'common/util/formulas';

import {
  getFinalDisplayOptions,
  getMetricFormattingDefaultsFromShowClause,
  metricFormatterFunctionForShowClause,
} from '../charts/util';
import {timestampToXauDateRange} from './time';

export enum ClauseType {
  Show = `show`,
  Formula = `formula`,
  Other = `other`,
}

export type BarChartTooltipStat = {
  count: number;
  secondaryCount?: number;
  dateRange: AbsoluteDateRange;
  percent: number;
  unit?: string;
  liftType?: LiftType;
};

type SharedClauseData = {
  metricKey: string;
};

export type FormulaClauseData = {
  clauseType: ClauseType.Formula;
} & FormulaClause &
  SharedClauseData;

export type ShowClauseData = {
  clauseType: ClauseType.Show;
  dataGroup?: DataGroup;
  resourceType?: InsightsResourceType | null;
  value?: {name: string};
} & ShowClause &
  SharedClauseData &
  BehaviorShowClause[`measurement`] &
  Omit<BehaviorShowClause[`behavior`], `type`>;

export type OtherClauseData = {
  clauseType: ClauseType.Other;
  // The metric clauses corresponding to the unselected "Other" segments
  metricClauses: MetricClauseData[];
} & SharedClauseData;

export type MetricClauseData = ShowClauseData | FormulaClauseData | OtherClauseData;

export function computeMetricClauseByKey({
  sectionsMetadata,
  bookmarkParams,
  dataGroups,
}: {
  sectionsMetadata: ReportSectionsMetadata;
  bookmarkParams: InsightsBookmarkParams;
  dataGroups: DataGroup[];
}): Record<string, MetricClauseData> {
  const metricClauseByKey: Record<string, MetricClauseData> = {};

  // for event comparison in Funnels metric, the sectionsMetadata are flat which means we do not know
  // which metric is part of the event comparisons. This information is important because we use `indices`
  // to map which show clause map to which metric name. Query API marks each metric key with `is_event_comparison`
  // Since all Metrics are ordered the same as show clauses (the reason why we can use indices to map things),
  // we can simply merge nearby metrics with `is_event_comparison` to one so that the existing indices mapping logic works.
  const groupedShowMetadata = groupEventComparisonShowMetadata(sectionsMetadata.show);

  if (sectionsMetadata.show) {
    groupedShowMetadata.forEach((metadata, index) => {
      const clauseAttrs = bookmarkParams.sections.show[index];

      if (isFormulaShowClause(clauseAttrs)) {
        if (metadata.metric_key) {
          metricClauseByKey[metadata.metric_key] = {
            ...omit(clauseAttrs, `type`),
            clauseType: ClauseType.Show,
            metricKey: metadata.metric_key,
          } as MetricClauseData;
        }
        return;
      }

      if (!isBehaviorShowClause(clauseAttrs)) {
        reporter.warn(`clauseAttrs is neither formula nor behavior show clause`, clauseAttrs);
        return;
      }

      const clauseDataGroupId = clauseAttrs.measurement.dataGroupId || clauseAttrs.behavior.dataGroupId;
      const dataGroup =
        clauseDataGroupId && dataGroups ? dataGroups.find((dg) => dg.groupID === clauseDataGroupId) : null;

      const metricClauseData: ShowClauseData = {
        ...omit(clauseAttrs, `type`),
        ...omit(clauseAttrs.behavior, `type`),
        ...clauseAttrs.measurement,
        ...(dataGroup ? {dataGroup} : {}),
        clauseType: ClauseType.Show,
        metricKey: Array.isArray(metadata) ? `` : metadata.metric_key,
      };

      if (metadata.sub_queries) {
        metadata.sub_queries
          .filter((subQuery) => subQuery.metric_key)
          .forEach((subQuery) => {
            metricClauseByKey[subQuery.metric_key] = {
              ...metricClauseData,
              metricKey: subQuery.metric_key,
            };
          });
      } else if (metadata) {
        if (Array.isArray(metadata)) {
          metadata.forEach((item) => {
            const newClauseData = cloneDeep(metricClauseData);
            newClauseData.metricKey = item.metric_key;
            metricClauseByKey[item.metric_key] = newClauseData;
          });
        } else {
          metricClauseByKey[metadata.metric_key] = metricClauseData;
        }
      }
    });
  }

  if (sectionsMetadata.formula) {
    sectionsMetadata.formula.forEach((metadata, index) => {
      if (metadata.metric_key) {
        metricClauseByKey[metadata.metric_key] = {
          ...bookmarkParams.sections.formula[index],
          clauseType: ClauseType.Formula,
          metricKey: metadata.metric_key,
        };
      }
    });
  }

  return metricClauseByKey;
}

type MetricUnitLabeler = (count) => Translatable;
type FormattedMetricUnit =
  | string
  | [
      string, // singular
      string, // plural
    ];

function getMetricTooltipItems(
  count: number,
  {
    formatter = (value) => commaizeNumber(value),
    metricUnit,
    shouldPluralize = true,
    shouldRound = false,
  }: {
    formatter?: (value: number) => string | number;
    metricUnit: MetricUnitLabeler | string;
    shouldPluralize?: boolean;
    shouldRound?: boolean;
  },
): TooltipItem[] {
  const text = formatter(shouldRound ? round(count, NUMERIC_DATA_PRECISION) : count) + ``;
  let unit;
  if (typeof metricUnit === `string`) {
    unit = shouldPluralize ? getPluralizedMetricUnit(count, metricUnit) : getSingularMetricUnit(metricUnit);
  } else if (typeof metricUnit === `function`) {
    unit = shouldPluralize ? metricUnit(count) : metricUnit(1);
  }
  return [
    {text, type: TooltipItemType.Data},
    ...(metricUnit
      ? [
          {
            text: unit,
            type: TooltipItemType.Text,
          },
        ]
      : []),
  ];
}

export function getSingularMetricUnit(metricUnit?: FormattedMetricUnit) {
  if (Array.isArray(metricUnit)) {
    return metricUnit.join(` `);
  }

  return metricUnit;
}

export function getPluralizedMetricUnit(count: number, metricUnit?: FormattedMetricUnit) {
  if (Array.isArray(metricUnit)) {
    return pluralize(metricUnit[0], count, metricUnit[1]);
  }

  return pluralize(metricUnit, count);
}

export type ComputeMetricNameRowsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
};

export function computeMetricNameRows({metricClauseData, segments}: ComputeMetricNameRowsParams): TooltipRow[] {
  const hasHistogramSegment =
    metricClauseData.clauseType === ClauseType.Show &&
    `math` in metricClauseData &&
    metricClauseData.math === PRIMITIVE_MATH_TYPES.HISTOGRAM;
  const breakdownSegments = hasHistogramSegment ? segments.slice(0, -1) : segments;
  const histogramSegment = hasHistogramSegment ? segments[segments.length - 1] : null;
  let metricDisplayName;
  if (metricClauseData.clauseType === ClauseType.Other) {
    const unselectedSegmentCount = metricClauseData.metricClauses.length;

    // If we have potentially hit the max amounts of segments, don't name how many, just say we have a lot of segments
    if (
      unselectedSegmentCount + REPORT_LIMITS[ReportName.Insights].segments[ChartType.Line].max >=
      REPORT_LIMITS[ReportName.Insights].query
    ) {
      metricDisplayName = `All other segments`;
    } else {
      metricDisplayName = `Other (${unselectedSegmentCount} unselected ${pluralize(`value`, unselectedSegmentCount)})`;
    }
  } else {
    metricDisplayName = metricClauseData.metricKey;
  }
  if (histogramSegment) {
    metricDisplayName = `${metricDisplayName} (${histogramSegment})`;
  }

  const rows: TooltipRow[] = [];

  rows.push({
    items: [
      {
        text: metricDisplayName,
        type: TooltipItemType.Strong,
      },
    ],
  });
  if (breakdownSegments && breakdownSegments.length) {
    const segmentText = breakdownSegments
      .map((segment) => (segment === OTHER_SEGMENT_VALUE ? `Other (unselected values)` : segment))
      .join(` / `);
    rows.push({
      items: [
        {
          text: truncateMiddle(segmentText, 45),
          type: TooltipItemType.Text,
        },
      ],
    });
  }

  return rows;
}

export function createMetricUnitLabeler(
  metricClauseData: MetricClauseData,
  overrideMath?: MATH_TYPE,
): MetricUnitLabeler {
  const emptyLabeler = () => () => ``;
  if (metricClauseData.clauseType === ClauseType.Other) {
    if (metricClauseData.metricClauses.length === 0) {
      return emptyLabeler;
    }
    const metricUnit = createMetricUnitLabeler(metricClauseData.metricClauses[0]);
    // Only show the unit if all the metrics in the "Other" segment are the same unit rather than mixed units
    return metricClauseData.metricClauses
      .slice(1)
      .every((clause) => isEqual(String(createMetricUnitLabeler(clause)), String(metricUnit)))
      ? metricUnit
      : emptyLabeler;
  } else if (metricClauseData.clauseType !== ClauseType.Show || isValidFormulaShowClause(metricClauseData)) {
    return () =>
      ({t}) =>
        t(`chart-actions.metrics.formula`);
  }

  let metricUnitLabeler: MetricUnitLabeler = emptyLabeler;
  const mathType = overrideMath || metricClauseData.math;
  const dataGroupName =
    metricClauseData.dataGroup && metricClauseData.dataGroupId ? metricClauseData.dataGroup.displayName : `user`;
  const dgContext = dataGroupName === `user` ? `user` : undefined;

  if (metricClauseData.resourceType === InsightsResourceType.Cohorts) {
    metricUnitLabeler =
      (count) =>
      ({t}) =>
        t(`chart-actions.metrics.group`, {context: dgContext, group: dataGroupName, count});
  } else if (metricClauseData.resourceType === InsightsResourceType.Events) {
    if (metricClauseData.perUserAggregation) {
      if (mathType === PRIMITIVE_MATH_TYPES.HISTOGRAM) {
        metricUnitLabeler =
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.group`, {context: dgContext, group: dataGroupName, count});
      } else if (metricClauseData.property) {
        metricUnitLabeler = dataGroupName
          ? (count) =>
              ({t}) =>
                t(`chart-actions.metrics.per`, {context: dgContext, group: dataGroupName, count})
          : null;
      } else if (metricClauseData.perUserAggregation === PER_USER_AGGREGATIONS.TOTAL) {
        metricUnitLabeler = dataGroupName
          ? (count) =>
              ({t}) =>
                t(`chart-actions.metrics.event-per`, {context: dgContext, group: dataGroupName, count})
          : null;
      }
    } else if (!metricClauseData.property) {
      metricUnitLabeler = {
        [XAU_MATH_TYPES.DAU]: dataGroupName
          ? (count) =>
              ({t}) =>
                t(`chart-actions.metrics.dau_average`, {context: dgContext, group: dataGroupName, count})
          : null,
        [XAU_MATH_TYPES.WAU]: dataGroupName
          ? (count) =>
              ({t}) =>
                t(`chart-actions.metrics.wau_average`, {context: dgContext, group: dataGroupName, count})
          : null,
        [XAU_MATH_TYPES.MAU]: dataGroupName
          ? (count) =>
              ({t}) =>
                t(`chart-actions.metrics.mau_average`, {context: dgContext, group: dataGroupName, count})
          : null,
        [PRIMITIVE_MATH_TYPES.SESSIONS]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.session`, {count}),
        [PRIMITIVE_MATH_TYPES.TOTAL]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.event`, {count}),
        [PRIMITIVE_MATH_TYPES.UNIQUE]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.group`, {context: dgContext, group: dataGroupName, count}),
        [FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.converted`, {count}),
        [FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.converted`, {count}),
        [FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.converted`, {count}),
        [FUNNELS_MATH_TYPES.GENERAL]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.total-conversion`, {count}),
        [FUNNELS_MATH_TYPES.SESSION]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.total-session`, {count}),
      }[mathType];
    } else if (metricClauseData.property?.name === FUNNELS_TIME_TO_CONVERSION.value) {
      // explicitly doing array to avoid pluralizing the `time to convert` phrase.
      metricUnitLabeler = {
        [PRIMITIVE_MATH_TYPES.AVERAGE]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.average-ttc`, {count}),
        [PRIMITIVE_MATH_TYPES.MEDIAN]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.median-ttc`, {count}),
        [PRIMITIVE_MATH_TYPES.MIN]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.minimum-ttc`, {count}),
        [PRIMITIVE_MATH_TYPES.MAX]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.maximum-ttc`, {count}),
        [PRIMITIVE_MATH_TYPES.P25]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.p25-ttc`, {count}),
        [PRIMITIVE_MATH_TYPES.P75]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.p75-ttc`, {count}),
        [PRIMITIVE_MATH_TYPES.P90]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.p90-ttc`, {count}),
        [PRIMITIVE_MATH_TYPES.P99]:
          (count) =>
          ({t}) =>
            t(`chart-actions.metrics.p99-ttc`, {count}),
      }[mathType];
    }
  } else if (
    metricClauseData.resourceType === InsightsResourceType.People &&
    !metricClauseData.property &&
    mathType === PRIMITIVE_MATH_TYPES.TOTAL
  ) {
    metricUnitLabeler = metricClauseData.dataGroupId
      ? (count) =>
          ({t}) =>
            t(`chart-actions.metrics.group`, {context: dgContext, group: dataGroupName, count})
      : (count) =>
          ({t}) =>
            t(`chart-actions.metrics.profile`, {count});
  }
  return metricUnitLabeler;
}

function getSecondaryMetricTooltipItems(value: number, metricClauseData: MetricClauseData) {
  const metricType =
    metricClauseData.clauseType === ClauseType.Show && `behavior` in metricClauseData
      ? metricClauseData.behavior.type
      : null;
  const mathOperator = `math` in metricClauseData ? metricClauseData.math : null;
  let secondaryMathOperator = null;
  if (metricType === MetricType.Funnel) {
    secondaryMathOperator = mathOperator
      ? {
          [FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE]: FUNNELS_MATH_TYPES.UNIQUE,
          [FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL]: FUNNELS_MATH_TYPES.GENERAL,
          [FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION]: FUNNELS_MATH_TYPES.SESSION,
          [FUNNELS_MATH_TYPES.UNIQUE]: FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
          [FUNNELS_MATH_TYPES.GENERAL]: FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
          [FUNNELS_MATH_TYPES.SESSION]: FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
        }[mathOperator]
      : null;
  } else if (metricType === MetricType.Retention) {
    secondaryMathOperator = mathOperator
      ? {
          [RETENTION_MATH_TYPES.RETENTION_RATE]: RETENTION_MATH_TYPES.UNIQUE,
          [RETENTION_MATH_TYPES.UNIQUE]: RETENTION_MATH_TYPES.RETENTION_RATE,
        }[mathOperator]
      : null;

    if (secondaryMathOperator === undefined) {
      return [];
    }
  }
  const secondaryMetricUnit = createMetricUnitLabeler(metricClauseData, secondaryMathOperator);

  return getMetricTooltipItems(value, {
    metricUnit: secondaryMetricUnit,
    formatter: [...FUNNELS_CONVERSION_RATES, RETENTION_MATH_TYPES.RETENTION_RATE].includes(
      secondaryMathOperator as FUNNELS_MATH_TYPES,
    )
      ? (val) => formatPercent(val)
      : (val) => commaizeNumber(val),
  });
}

export type ComputeBarChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: BarChartTooltipStat;
  timeComparisonStat?: BarChartTooltipStat;
  liftComparisonStat?: BarChartTooltipStat;
  baselineComparisonStat?: BarChartTooltipStat;
  math?: MATH_TYPE;
  titleColor?: string;
  formatter: (value: number) => string;
  i18n: LocalizationStore;
};

export function computeBarChartTooltipSections(params: ComputeBarChartTooltipSectionsParams): TooltipSection[] {
  const {
    segments,
    metricClauseData,
    stat,
    timeComparisonStat,
    liftComparisonStat,
    baselineComparisonStat,
    titleColor,
    formatter,
    i18n,
  } = params;

  const metricUnit = createMetricUnitLabeler(metricClauseData);
  const metricNameRows: TooltipRow[] = computeMetricNameRows({metricClauseData, segments});

  const sections: TooltipSection[] = [{rows: metricNameRows}];

  if (timeComparisonStat && liftComparisonStat) {
    const [formattedDateRange, formattedTimeComparisonDateRange] = formatDateRanges([
      stat.dateRange,
      timeComparisonStat.dateRange,
    ]);

    const tooltipRows: TooltipRow[] = [
      {
        items: [
          {
            text: `${liftComparisonStat.percent > 0 ? `+` : ``}${round(
              liftComparisonStat.percent,
              NUMERIC_DATA_PRECISION,
            ).toString()}%`,
            type: TooltipItemType.Data,
            theme: liftComparisonStat.percent < 0 ? TEXT_HIGHLIGHT_THEME.NEGATIVE : TEXT_HIGHLIGHT_THEME.POSITIVE,
          },
        ],
      },
      {
        items: getMetricTooltipItems(stat.count, {
          formatter,
          metricUnit: formattedDateRange,
          shouldPluralize: false,
        }),
      },
      {
        items: getMetricTooltipItems(timeComparisonStat.count, {
          formatter,
          metricUnit: formattedTimeComparisonDateRange,
          shouldPluralize: false,
        }),
      },
    ];

    sections.push({rows: tooltipRows});
  } else if (timeComparisonStat) {
    const [formattedDateRange, formattedTimeComparisonDateRange] = formatDateRanges([
      stat.dateRange,
      timeComparisonStat.dateRange,
    ]);

    const delta = timeComparisonStat.count === 0 ? 1 : computeDelta(stat.count, timeComparisonStat.count);
    const formattedDelta = formatPercent(delta);

    const tooltipRows: TooltipRow[] = [
      {
        items: getMetricTooltipItems(stat.count, {metricUnit, formatter}),
      },
      ...(stat.secondaryCount
        ? [
            {
              items: getSecondaryMetricTooltipItems(stat.secondaryCount, metricClauseData),
            },
          ]
        : []),
      {
        items: [
          {
            text: delta > 0 ? `+${formattedDelta}` : formattedDelta,
            theme: delta < 0 ? TEXT_HIGHLIGHT_THEME.NEGATIVE : TEXT_HIGHLIGHT_THEME.POSITIVE,
            type: TooltipItemType.Data,
          },
          {
            text: i18n.t(`chart-actions.tooltip.from-previous-period`),
            type: TooltipItemType.Text,
          },
        ],
      },
    ];
    sections.push(
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Solid,
        rows: [
          {
            items: [
              {
                text: formattedDateRange,
                type: TooltipItemType.Detail,
              },
            ],
          },
          ...tooltipRows,
        ],
      },
      {rows: []}, // for additional spacing to emphasize separation between sections
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Dash,
        rows: [
          {
            items: [
              {
                text: formattedTimeComparisonDateRange,
                type: TooltipItemType.Detail,
              },
            ],
          },
          {
            items: getMetricTooltipItems(timeComparisonStat.count, {metricUnit, formatter}),
          },
          ...(stat.secondaryCount
            ? [
                {
                  items: getSecondaryMetricTooltipItems(timeComparisonStat.secondaryCount, metricClauseData),
                },
              ]
            : []),
        ],
      },
    );
  } else if (liftComparisonStat && `math` in metricClauseData) {
    const tooltipRows = generateLiftOverallAndBaselineRows({
      baselineComparisonStat,
      formatter,
      liftComparisonStat,
      math: metricClauseData.math,
      metricUnit,
      stat,
    });

    sections.push({rows: tooltipRows});
  } else {
    sections.push({
      rows: [
        {
          items: getMetricTooltipItems(stat.count, {metricUnit, formatter}),
        },
        ...(stat.secondaryCount
          ? [
              {
                items: getSecondaryMetricTooltipItems(stat.secondaryCount, metricClauseData),
              },
            ]
          : []),
        {
          items: [
            {
              text: formatPercent(stat.percent / 100),
              type: TooltipItemType.Data,
            },
            {
              text: i18n.t(`chart-actions.tooltip.of-overall`),
              type: TooltipItemType.Text,
            },
          ],
        },
      ],
    });
  }

  return sections;
}

function generateLiftOverallAndBaselineRows(params: {
  baselineComparisonStat: BarChartTooltipStat;
  liftComparisonStat: BarChartTooltipStat;
  formatter: (value: number) => string;
  math: MATH_TYPE;
  metricUnit: MetricUnitLabeler;
  stat: BarChartTooltipStat;
}) {
  const {baselineComparisonStat, formatter, liftComparisonStat, math, metricUnit, stat} = params;
  const isOverall = liftComparisonStat.liftType === LiftType.Overall;

  const liftValue = liftComparisonStat.percent;
  const liftFormattedText = `${liftValue >= 0 && !isOverall ? `+` : ``}${round(
    liftValue,
    NUMERIC_DATA_PRECISION,
  ).toString()}%`;

  let baselineComparisonName = baselineComparisonStat?.unit ?? metricUnit;
  let theme: TEXT_HIGHLIGHT_THEME = liftValue < 0 ? TEXT_HIGHLIGHT_THEME.NEGATIVE : TEXT_HIGHLIGHT_THEME.POSITIVE;

  if (isOverall) {
    // Since overall % is neither positive nor negative, we want a neutral theme (i.e. none)
    theme = TEXT_HIGHLIGHT_THEME.GENERIC;
    baselineComparisonName = `${capitalize(EVENT_MATH_LABELS[math])} ${baselineComparisonName}`;
  }

  return [
    {
      items: [
        {
          text: liftFormattedText,
          type: TooltipItemType.Data,
          theme,
        },
      ],
    },
    {
      items: getMetricTooltipItems(stat.count, {
        formatter,
        metricUnit: liftComparisonStat?.unit ?? metricUnit,
        shouldPluralize: false,
      }),
    },
    {
      items: getMetricTooltipItems(baselineComparisonStat?.count || 0, {
        formatter,
        metricUnit: baselineComparisonName ?? metricUnit,
        shouldPluralize: false,
      }),
    },
  ];
}

export type ComputeMetricChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  value: number;
  secondaryValue?: number;
  formatter: (value: number) => string | number;
};

export function computeMetricChartTooltipSections(params: ComputeMetricChartTooltipSectionsParams): TooltipSection[] {
  const {metricClauseData, segments, value, secondaryValue, formatter} = params;

  return [
    {
      rows: computeMetricNameRows({metricClauseData, segments}),
    },
    {
      rows: [
        {
          items: getMetricTooltipItems(value, {
            metricUnit: createMetricUnitLabeler(metricClauseData),
            formatter,
          }),
        },
        ...(secondaryValue
          ? [
              {
                items: getSecondaryMetricTooltipItems(secondaryValue, metricClauseData),
              },
            ]
          : []),
      ],
    },
  ];
}

function timestampToXauDateRangeFunction({
  unit,
  operator,
  isLastInterval = false,
  lastIntervalToDate = null,
  timezone,
}: {
  unit: TimeUnit;
  operator: MATH_TYPE;
  isLastInterval?: boolean;
  lastIntervalToDate?: string;
  timezone: string;
}) {
  // These are the only units allowed in XAU queries, and the only ones this function handles
  const ALLOWED_UNITS = [UNITS[`day`], UNITS[`week`], UNITS[`month`]];
  if (!ALLOWED_UNITS.includes(unit)) {
    reporter.error(new Error(`Unit not in ${ALLOWED_UNITS}: "${unit}"`));
  }

  return function (timestamp, {isTimeComparison = false} = {}) {
    const {fromDate, toDate} = timestampToXauDateRange(timestamp, {
      unit,
      operator,
      isLastInterval: isTimeComparison ? false : isLastInterval,
      lastIntervalToDate,
    });
    return `${formatDate(fromDate, {unit: `week`, timezone, displayRangeIfWeek: false})} - ${formatDate(toDate, {unit: `week`, timezone, displayRangeIfWeek: false})}`;
  };
}

export function timestampToTimeUnitFunction({
  displayRangeIfWeek = true,
  unit,
}: {
  displayRangeIfWeek?: boolean;
  unit: string;
}) {
  return (timestamp) => formatDate(timestamp, {unit, displayRangeIfWeek});
}

export type LineChartTooltipStat<ValueT = number> = {
  value: ValueT;
  secondaryValue?: ValueT;
  /**
   * This is the timestamp that is formatted to trick Highcharts into displaying
   * times in the project timezone instead of local timezone. It is NOT the actual
   * UTC timestamp for the data point. Use this for Highcharts.
   */
  highchartsTimestamp?: number;
  /**
   * This is the real UTC timestamp for the data point. Use this to display
   * the actual time to the user. Do NOT pass this to Highcharts.
   */
  timestamp?: number;
  dateRange?: AbsoluteDateRange;
  segmentKey?: string;
};

export type ComputeLineChartTooltipSectionsParams = {
  timezone: string;
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: LineChartTooltipStat;
  deltaStat?: LineChartTooltipStat;
  timeComparisonStat?: LineChartTooltipStat;
  liftComparisonStat?: LineChartTooltipStat;
  baselineComparisonStat?: LineChartTooltipStat;
  overallComparisonStat?: LineChartTooltipStat;
  forecastComparisonStat?: LineChartTooltipStat<ForecastValue>;
  timeUnit: TimeUnit;
  titleColor: string;
  // Used in stacked line chart mode
  percent?: number;
  isLastInterval: boolean;
  formatter?: (value: number) => string;
  i18n: LocalizationStore;
};

export function computeLineChartTooltipSections(params: ComputeLineChartTooltipSectionsParams) {
  const {
    timezone,
    segments,
    metricClauseData,
    stat,
    deltaStat,
    timeComparisonStat,
    liftComparisonStat,
    baselineComparisonStat,
    overallComparisonStat,
    forecastComparisonStat,
    percent,
    timeUnit,
    isLastInterval,
    titleColor,
    formatter,
    i18n,
  } = params;

  const formatDelta = (delta) => {
    if (!isDefinedValue(delta)) {
      return i18n.t(`chart-actions.tooltip.not-available`);
    }
    const sign = delta > 0 ? `+` : ``;
    return `${sign}${formatPercent(delta)}`;
  };

  const theme = (delta): TEXT_HIGHLIGHT_THEME => {
    if (!isDefinedValue(delta) || delta === 0 || Boolean(overallComparisonStat)) {
      return TEXT_HIGHLIGHT_THEME.GENERIC;
    }
    return delta > 0 ? TEXT_HIGHLIGHT_THEME.POSITIVE : TEXT_HIGHLIGHT_THEME.NEGATIVE;
  };

  const metricUnit = createMetricUnitLabeler(metricClauseData);
  const metricNameRows: TooltipRow[] = computeMetricNameRows({metricClauseData, segments});

  let timestampFormatter;
  // the operators that require a special date range calculation
  const xauDateRangeOperators: MATH_TYPE[] = [XAU_MATH_TYPES.MAU, XAU_MATH_TYPES.WAU];
  if (
    metricClauseData.clauseType === ClauseType.Show &&
    `math` in metricClauseData &&
    xauDateRangeOperators.includes(metricClauseData.math)
  ) {
    timestampFormatter = timestampToXauDateRangeFunction({
      operator: metricClauseData.math,
      unit: timeUnit,
      isLastInterval,
      lastIntervalToDate: stat.dateRange.toDate,
      timezone,
    });
  } else {
    timestampFormatter = (timestamp) => formatDate(timestamp, {unit: timeUnit, timezone});
  }

  const sections: TooltipSection[] = [];
  if (!timeComparisonStat) {
    sections.push({
      rows: [
        ...metricNameRows,
        {
          items: [
            {
              text: timestampFormatter(stat.timestamp),
              type: TooltipItemType.Detail,
            },
          ],
        },
      ],
    });
  } else {
    sections.push({rows: metricNameRows});
  }

  if (liftComparisonStat) {
    const liftTooltipRows = liftComparisonTooltips(
      stat,
      liftComparisonStat,
      theme,
      timestampFormatter,
      baselineComparisonStat,
      overallComparisonStat,
      timeComparisonStat,
      formatter,
    );

    sections.push({rows: liftTooltipRows});
  } else if (timeComparisonStat) {
    const tooltipRows: TooltipRow[] = [
      {
        items: getMetricTooltipItems(stat.value, {metricUnit, shouldRound: true, formatter}),
      },
      ...(stat.secondaryValue
        ? [
            {
              items: getSecondaryMetricTooltipItems(stat.secondaryValue, metricClauseData),
            },
          ]
        : []),
    ];

    if (Number.isFinite(timeComparisonStat.value)) {
      // May be null when time comparison data is incomplete due to a new project/ data loss/ etc
      const delta = computeDelta(stat.value, timeComparisonStat.value);
      tooltipRows.push({
        items: [
          {
            text: formatDelta(delta),
            type: TooltipItemType.Data,
            theme: theme(delta),
          },
          {
            text: i18n.t(`chart-actions.tooltip.from-previous-period`),
            type: TooltipItemType.Text,
          },
        ],
      });
    }

    sections.push(
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Solid,
        rows: [
          {
            items: [
              {
                text: timestampFormatter(stat.timestamp),
                type: TooltipItemType.Detail,
              },
            ],
          },
        ].concat(tooltipRows),
      },
      {rows: []}, // for additional spacing for extra emphasis in this case
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Dash,
        rows: [
          {
            items: [
              {
                text: timestampFormatter(timeComparisonStat.timestamp, {isTimeComparison: true}),
                type: TooltipItemType.Detail,
              },
            ],
          },
          {
            items: getMetricTooltipItems(timeComparisonStat.value, {metricUnit, shouldRound: true, formatter}),
          },
          ...(timeComparisonStat.secondaryValue
            ? [
                {
                  items: getSecondaryMetricTooltipItems(timeComparisonStat.secondaryValue, metricClauseData),
                },
              ]
            : []),
        ],
      },
    );
  } else if (forecastComparisonStat) {
    const forecastTooltipRows = forecastComparisonTooltips(stat, forecastComparisonStat, metricUnit, formatDelta, i18n);

    sections.push({rows: forecastTooltipRows});
  } else {
    const tooltipRows: TooltipRow[] = [
      {
        items: getMetricTooltipItems(stat.value, {metricUnit, shouldRound: true, formatter}),
      },
    ];
    if (stat.secondaryValue) {
      tooltipRows.push({
        items: getSecondaryMetricTooltipItems(stat.secondaryValue, metricClauseData),
      });
    }

    if (Number.isFinite(percent)) {
      tooltipRows.push({
        items: [
          {
            text: formatPercent(percent / 100),
            type: TooltipItemType.Data,
          },
          {
            text: i18n.t(`chart-actions.tooltip.of-total`),
            type: TooltipItemType.Text,
          },
        ],
      });
    } else if (Number.isFinite(deltaStat?.value)) {
      // May be null when time comparison data is incomplete due to a new project/ data loss/ etc
      const delta = computeDelta(stat.value, deltaStat.value);
      tooltipRows.push({
        items: [
          {
            text: formatDelta(delta),
            type: TooltipItemType.Data,
            theme: theme(delta),
          },
          {
            text: i18n.t(`chart-actions.tooltip.from-previous-time-unit`, {
              timeUnit: i18n.print(TimeUnitLabelTranslatable[timeUnit](1)).toLocaleLowerCase(),
            }),
            type: TooltipItemType.Text,
          },
        ],
      });
    }

    sections.push({rows: tooltipRows});
  }

  return sections;
}

function forecastComparisonTooltips(
  stat: LineChartTooltipStat<number>,
  forecastComparisonStat: LineChartTooltipStat<ForecastValue>,
  metricUnit: MetricUnitLabeler | string,
  formatDelta: {(delta: any): string; (arg0: number): any},
  i18n: LocalizationStore,
) {
  const tooltipRows: TooltipRow[] = [
    {
      items: getMetricTooltipItems(stat.value, {metricUnit, shouldRound: true}),
    },
  ];
  const outsideExpectedRange =
    stat.value > forecastComparisonStat.value.yhat_upper || stat.value < forecastComparisonStat.value.yhat_lower;
  if (outsideExpectedRange) {
    const outsideRangeValue =
      stat.value > forecastComparisonStat.value.yhat_upper
        ? forecastComparisonStat.value.yhat_upper
        : forecastComparisonStat.value.yhat_lower;
    const delta = computeDelta(stat.value, outsideRangeValue);
    tooltipRows.push({
      items: [
        {
          text: formatDelta(delta),
          type: TooltipItemType.Data,
          theme: TEXT_HIGHLIGHT_THEME.NEGATIVE,
        },
        {
          text:
            delta > 0 ? i18n.t(`chart-actions.tooltip.above-expected`) : i18n.t(`chart-actions.tooltip.below-expected`),
          type: TooltipItemType.Text,
        },
      ],
    });
  }
  tooltipRows.push({
    items: [
      {
        text: commaizeNumber(forecastComparisonStat.value.yhat_upper, {maxPrecision: 2}),
        type: TooltipItemType.Data,
      },
      {
        text: i18n.t(`chart-actions.tooltip.high`),
        type: TooltipItemType.Text,
      },
    ],
  });
  tooltipRows.push({
    items: [
      {
        text: commaizeNumber(forecastComparisonStat.value.yhat_lower, {maxPrecision: 2}),
        type: TooltipItemType.Data,
      },
      {
        text: i18n.t(`chart-actions.tooltip.low`),
        type: TooltipItemType.Text,
      },
    ],
  });

  return tooltipRows;
}

function liftComparisonTooltips(
  stat,
  liftComparisonStat,
  theme,
  timestampFormatter,
  baselineComparisonStat,
  overallComparisonStat,
  timeComparisonStat,
  formatter: (val: number) => string,
): TooltipRow[] {
  const liftValue = liftComparisonStat.value;
  const liftFormattedText = `${liftValue >= 0 && !overallComparisonStat ? `+` : ``}${round(
    liftValue * 100,
    NUMERIC_DATA_PRECISION,
  ).toString()}%`;
  const tooltipRows: TooltipRow[] = [
    {
      items: [
        {
          text: liftFormattedText,
          type: TooltipItemType.Data,
          theme: theme(liftValue),
        },
      ],
    },
    {
      items: getMetricTooltipItems(stat.value, {
        metricUnit: timeComparisonStat ? timestampFormatter(stat.timestamp) : liftComparisonStat.segmentKey,
        shouldPluralize: false,
        shouldRound: true,
        formatter,
      }),
    },
  ];

  if (timeComparisonStat) {
    tooltipRows.push({
      items: getMetricTooltipItems(timeComparisonStat?.value, {
        metricUnit: timestampFormatter(timeComparisonStat.timestamp),
        shouldPluralize: false,
        shouldRound: true,
        formatter,
      }),
    });
  }

  if (overallComparisonStat) {
    tooltipRows.push({
      items: getMetricTooltipItems(overallComparisonStat?.value, {
        metricUnit: overallComparisonStat.segmentKey,
        shouldPluralize: false,
        shouldRound: true,
        formatter,
      }),
    });
  } else if (baselineComparisonStat) {
    const lastSegment: string = baselineComparisonStat.segmentKey.split(`,`).pop().toString();
    tooltipRows.push({
      items: getMetricTooltipItems(baselineComparisonStat?.value, {
        metricUnit: lastSegment,
        shouldPluralize: false,
        shouldRound: true,
        formatter,
      }),
    });
  }

  return tooltipRows;
}

export type RenderLineChartTooltipParams = {
  timezone: string;
  headers?: string[];
  metricClauseData: MetricClauseData;
  metricsResource: MetricsResource;
  segments: string[];
  stat: LineChartTooltipStat;
  deltaStat?: LineChartTooltipStat;
  timeComparisonStat?: LineChartTooltipStat;
  liftComparisonStat?: LineChartTooltipStat;
  baselineComparisonStat?: LineChartTooltipStat;
  overallComparisonStat?: LineChartTooltipStat;
  forecastComparisonStat?: LineChartTooltipStat<ForecastValue>;
  timeUnit: TimeUnit;
  titleColor: string;
  percent?: number;
  isFirstInterval: boolean;
  isLastInterval: boolean;
  isIncomplete: boolean;
  utcOffset: number;
  isComparisonItem?: boolean;
  i18n: LocalizationStore;
};

export function getLineChartTooltipProps({
  timezone,
  metricClauseData,
  metricsResource,
  segments,
  stat,
  deltaStat,
  timeComparisonStat,
  liftComparisonStat,
  baselineComparisonStat,
  overallComparisonStat,
  forecastComparisonStat,
  timeUnit,
  titleColor,
  percent,
  isFirstInterval,
  isLastInterval,
  isIncomplete,
  utcOffset,
  i18n,
}: RenderLineChartTooltipParams): ChartTooltipProps {
  const mathOperator =
    metricClauseData.clauseType === ClauseType.Show && `math` in metricClauseData ? metricClauseData.math : null;

  const lastIntervalToDate = stat.dateRange.toDate;
  if (isFirstInterval) {
    isIncomplete = isIncompleteFirstInterval(stat.highchartsTimestamp, stat.dateRange.fromDate, {
      mathOperator,
      utcOffset,
    });
  } else if (isLastInterval) {
    isIncomplete = isIncompleteLastInterval(stat.highchartsTimestamp, lastIntervalToDate, {
      mathOperator,
      unit: timeUnit,
      utcOffset,
    });
  }

  if (timeComparisonStat) {
    if (isFirstInterval) {
      isIncomplete =
        isIncomplete ||
        isIncompleteFirstInterval(timeComparisonStat.highchartsTimestamp, timeComparisonStat.dateRange.fromDate, {
          mathOperator,
          utcOffset,
        });
    } else if (isLastInterval) {
      isIncomplete =
        isIncomplete ||
        isIncompleteLastInterval(timeComparisonStat.highchartsTimestamp, timeComparisonStat.dateRange.toDate, {
          mathOperator,
          unit: timeUnit,
          utcOffset,
        });
    }
  }

  const sections = computeLineChartTooltipSections({
    timezone,
    metricClauseData,
    segments,
    stat,
    deltaStat,
    timeComparisonStat,
    liftComparisonStat,
    baselineComparisonStat,
    overallComparisonStat,
    forecastComparisonStat,
    timeUnit,
    titleColor,
    percent,
    isLastInterval,
    formatter: (value) => {
      const showClause = metricClauseData.clauseType === ClauseType.Show ? metricClauseData : null;
      const formattedValue = metricFormatterFunctionForShowClause(showClause)(value);
      const defaultOptions = getMetricFormattingDefaultsFromShowClause(showClause);
      return formatMetricNumberWithUnits(
        formattedValue,
        getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, metricsResource, defaultOptions, {
          // Tables and tooltip values should never be abbreviated.
          abbrev: false,
        }),
      );
    },
    i18n,
  });

  return {
    titleColor: !timeComparisonStat ? titleColor : ``,
    footerText: isIncomplete ? i18n.t(`chart-actions.tooltip.incomplete-time-unit`, {timeUnit}) : null,
    sections,
  };
}

export function renderLineChartTooltip({
  timezone,
  metricClauseData,
  metricsResource,
  segments,
  stat,
  deltaStat,
  timeComparisonStat,
  liftComparisonStat,
  baselineComparisonStat,
  overallComparisonStat,
  forecastComparisonStat,
  timeUnit,
  titleColor,
  percent,
  isFirstInterval,
  isLastInterval,
  isIncomplete,
  utcOffset,
  i18n,
}: RenderLineChartTooltipParams): string {
  const tooltipProps = getLineChartTooltipProps({
    timezone,
    metricClauseData,
    metricsResource,
    segments,
    stat,
    deltaStat,
    timeComparisonStat,
    liftComparisonStat,
    baselineComparisonStat,
    overallComparisonStat,
    forecastComparisonStat,
    timeUnit,
    titleColor,
    percent,
    isFirstInterval,
    isLastInterval,
    isIncomplete,
    utcOffset,
    i18n,
  });
  return renderChartTooltipToString(tooltipProps);
}

export type PieChartTooltipStat = {
  count: number;
  percent: number;
};

export type ComputePieChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: PieChartTooltipStat;
  formatter: (value: number) => string;
  i18n: LocalizationStore;
};

export function computePieChartTooltipSections(params: ComputePieChartTooltipSectionsParams) {
  const {formatter, metricClauseData, segments, stat, i18n} = params;

  return [
    {
      rows: computeMetricNameRows({metricClauseData, segments}),
    },
    {
      rows: [
        {
          items: getMetricTooltipItems(stat.count, {formatter, metricUnit: createMetricUnitLabeler(metricClauseData)}),
        },
        {
          items: [
            {
              text: formatPercent(stat.percent / 100),
              type: TooltipItemType.Data,
            },
            {
              text: i18n.t(`chart-actions.tooltip.of-total`),
              type: TooltipItemType.Text,
            },
          ],
        },
      ],
    },
  ];
}

type RenderPieChartTooltipParams = {
  color: string;
  formatter: (value: number) => string;
  metricClauseByKey: Record<string, MetricClauseData>;
  i18n: LocalizationStore;
  metricKey?: string;
  otherMetricKeys?: string[];
  percent: number;
  segments?: string[];
  value: number;
};

export function renderPieChartTooltip({
  color,
  formatter,
  metricClauseByKey,
  i18n,
  metricKey,
  otherMetricKeys,
  percent,
  segments,
  value,
}: RenderPieChartTooltipParams): string {
  let metricClauseData: MetricClauseData;
  if (otherMetricKeys) {
    metricClauseData = {
      clauseType: ClauseType.Other,
      metricClauses: otherMetricKeys.map((otherMetricKey) => metricClauseByKey[otherMetricKey]),
      metricKey: OTHER_SEGMENT_VALUE,
    };
    segments = [];
  } else {
    metricClauseData = metricClauseByKey[metricKey];
  }

  const sections = computePieChartTooltipSections({
    formatter,
    metricClauseData,
    segments,
    stat: {
      count: value,
      percent,
    },
    i18n,
  });

  return renderChartTooltipToString({
    sections,
    titleColor: color,
  });
}
